import { createFileRoute, useNavigate, useLocation } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { ClientFormModal } from '../components/ClientFormModal';
import api from '../lib/api';
import { CreateClientDto, UpdateClientDto } from '@prometeus/contracts';
import { usePermissionGuard } from '../hooks/usePermissionGuard';
import { usePermissions } from '../hooks/usePermissions';
import { usePermissionDeniedStore } from '../stores/permissionDenied.store';

export const Route = createFileRoute('/w/$workspaceId/clients/new')({
  component: NewClientPage,
});

function NewClientPage() {
  const { workspaceId } = Route.useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { requirePermission } = usePermissionGuard();
  const { hasPermission, isLoading: isLoadingPermissions } = usePermissions();
  const showPermissionDenied = usePermissionDeniedStore((state) => state.show);
  const canCreate = hasPermission('clients.create');

  useEffect(() => {
    if (!location.pathname.startsWith('/w/')) return;
    if (!isLoadingPermissions && !canCreate) {
      showPermissionDenied('Criar clientes');
      navigate({ to: '/w/$workspaceId/clients', params: { workspaceId } });
    }
  }, [location.pathname, isLoadingPermissions, canCreate, showPermissionDenied, navigate, workspaceId]);

  const createMutation = useMutation({
    mutationFn: async (data: CreateClientDto) => {
      const response = await api.post('/clients', data);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients', workspaceId] });
      navigate({ to: `/w/${workspaceId}/clients` });
    },
  });

  const handleSave = (data: CreateClientDto | UpdateClientDto) => {
    if (!requirePermission('clients.create', 'Criar clientes')) return;
    // When creating, type and name are always required (CreateClientDto)
    if (data.type && data.name) {
      createMutation.mutate(data as CreateClientDto);
    }
  };

  const handleClose = () => {
    navigate({ to: `/w/${workspaceId}/clients` });
  };

  if (!isLoadingPermissions && !canCreate) return null;

  return (
    <ClientFormModal
      workspaceId={workspaceId}
      onClose={handleClose}
      onSave={handleSave}
      isSaving={createMutation.isPending}
    />
  );
}

